import firebase from 'firebase/app';

/**
 * One-off reads for the admin screens, answered by the database itself.
 *
 * A plain query `.get()` falls back to the browser's own copy when the database can't be reached
 * in time, and that copy is usually empty. Screens then showed zeros or "Nothing here" with no
 * error. Seen on live, 5 Nov 2026: Firestore logged "Could not reach Cloud Firestore backend…
 * within 10 seconds" and Admin › Overview showed 0 for everything until Refresh.
 *
 * `serverQuery` waits for the database's own answer (up to `timeoutMs`; it keeps trying while the
 * connection recovers) and otherwise fails with OFFLINE_MESSAGE, so the screen can say so.
 */
export const OFFLINE_MESSAGE = "Couldn't reach the database. Check the internet connection, then try again.";

const WAIT_MS = 30000;

export const serverQuery = (query: firebase.firestore.Query, timeoutMs = WAIT_MS): Promise<firebase.firestore.QuerySnapshot> =>
  new Promise((resolve, reject) => {
    let settled = false;
    let stop: (() => void) | null = null;
    const finish = (then: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      if (stop) stop();
      then();
    };
    const timer = setTimeout(() => finish(() => reject(new Error(OFFLINE_MESSAGE))), timeoutMs);
    stop = query.onSnapshot(
      { includeMetadataChanges: true },
      (snap) => {
        // The first answer may be the browser's copy; wait for the one from the database.
        if (!snap.metadata.fromCache) finish(() => resolve(snap));
      },
      (err) => finish(() => reject(err)),
    );
    if (settled) stop();
  });
